import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_design-fasaad/artifacts/wjth3ypm_ChatGPT%20Image%20Mar%203%2C%202026%2C%2002_08_01%20PM.png";

export const About = () => {
  return (
    <div data-testid="about-page" className="relative">
      {/* Fixed Full Screen Background Image - Always Visible */}
      <div className="fixed inset-0 z-0">
        <img 
          src={FOUNDER_IMAGE}
          alt="Faiqa Asghar and Saad Ahmad"
          className="w-full h-full object-cover object-top grayscale"
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Scrolling Content Overlay */}
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="min-h-screen flex items-end">
          <div className="w-full max-w-screen-2xl mx-auto px-16 lg:px-40 pb-32 pt-48">
            <div className="recognition-badge bg-black/30 backdrop-blur-sm border-white/30 text-white/90 mb-8">
              <Award size={16} />
              <span>25 Under 25 Global Talents — Innovation 2020</span>
            </div>
            <h1 className="font-['Syne'] font-semibold text-6xl lg:text-9xl text-white leading-none" data-testid="about-title">
              The Studio
            </h1>
          </div>
        </div>

        {/* Who We Are - Frosted glass overlay */}
        <section className="py-32 px-16 lg:px-40" data-testid="philosophy-section">
          <div className="max-w-screen-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-12 lg:p-20 shadow-2xl">
              <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-3">
                  <span className="label-caps block text-white/60">Who We Are</span>
                </div>
                <div className="lg:col-span-9">
                  <p className="about-text mb-10 text-white/90">
                    FaSaad Mimar is an award-winning architecture and design studio 
                    based in Istanbul. We represent a new generation of architectural 
                    thinking—where research meets creativity, and innovation serves humanity.
                  </p>
                  <p className="about-text mb-10 text-white/90">
                    Every project begins with rigorous research and client analysis, 
                    ensuring designs that precisely meet functional requirements while 
                    pushing creative boundaries. Our name reflects our collaborative 
                    philosophy—a fusion of vision and purpose.
                  </p>
                  <p className="about-text text-white/50">
                    With international recognition including the Dubai D3 Pavilion 
                    Design competition and award-winning pandemic-era restaurant 
                    design, our work spans scales from intimate furniture pieces to 
                    large-scale architectural interventions—always with an eye for 
                    detail and a commitment to human-centered design.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-32 px-16 lg:px-40" data-testid="services-section">
          <div className="max-w-screen-2xl mx-auto">
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 p-12 lg:p-20 shadow-2xl">
              <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-3">
                  <span className="label-caps block text-white/60">What We Do</span>
                </div>
                <div className="lg:col-span-9">
                  <div className="space-y-0">
                    {[
                      {
                        title: "Architecture",
                        desc: "Residential, commercial, cultural, and public spaces designed with purpose and precision."
                      },
                      {
                        title: "Interior Architecture",
                        desc: "Curated environments that balance aesthetics with functionality, from hospitality to residential."
                      },
                      {
                        title: "Furniture Design",
                        desc: "Bespoke pieces and limited editions that serve as functional art within your space."
                      }
                    ].map((service, i) => (
                      <div key={service.title} className="py-10 border-b border-white/10 last:border-0">
                        <div className="flex items-start gap-8">
                          <span className="font-['Syne'] font-semibold text-4xl text-white/20">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <div>
                            <h3 className="font-['Syne'] font-semibold text-2xl mb-3 text-white">{service.title}</h3>
                            <p className="text-white/50 max-w-lg">{service.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="py-32 px-16 lg:px-40" data-testid="values-section">
          <div className="max-w-screen-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-12 lg:p-20 shadow-2xl">
              <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-3">
                  <span className="label-caps block text-white/60">Our Approach</span>
                </div>
                <div className="lg:col-span-9">
                  <p className="about-text mb-16 text-white/90">
                    We believe great architecture emerges from deep understanding. 
                    Before sketching a single line, we immerse ourselves in 
                    research—studying user behaviors, environmental contexts, and 
                    cultural narratives. This foundation ensures our designs are 
                    not just beautiful, but meaningful and enduring.
                  </p>
                  
                  <div className="grid sm:grid-cols-3 gap-12">
                    {[
                      { title: "Research", desc: "Evidence-based design methodology" },
                      { title: "Collaboration", desc: "Working closely with clients" },
                      { title: "Precision", desc: "Every detail considered" }
                    ].map((value) => (
                      <div key={value.title}>
                        <h4 className="font-['Syne'] font-semibold text-lg mb-2 text-white">{value.title}</h4>
                        <p className="text-sm text-white/50">{value.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 px-16 lg:px-40" data-testid="about-cta">
          <div className="max-w-screen-2xl mx-auto">
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-12 lg:p-20 text-white shadow-2xl">
              <div className="max-w-3xl">
                <h2 className="font-['Syne'] font-semibold text-4xl lg:text-5xl mb-8">
                  Ready to Create Together?
                </h2>
                <p className="text-white/60 text-lg mb-12">
                  Whether you're planning a new build, renovation, or seeking design 
                  consultation, we'd love to hear about your project.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to="/contact" 
                    className="btn-editorial bg-white text-[#0A0A0A] hover:bg-transparent hover:text-white hover:shadow-[inset_0_0_0_1px_white]"
                    data-testid="contact-btn"
                  >
                    Get in Touch
                    <ArrowRight size={16} />
                  </Link>
                  <Link 
                    to="/appointment" 
                    className="btn-outline border-white text-white hover:bg-white hover:text-[#0A0A0A]"
                    data-testid="book-btn"
                  >
                    Book Consultation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Spacer for footer */}
        <div className="h-32 bg-[#0A0A0A]/80 backdrop-blur-xl"></div>
      </div>
    </div>
  );
};
